import { useEffect, useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { computeSession, SessionStatus } from '../utils/session-window';

const COLOR_CLASS: Record<SessionStatus['color'], string> = {
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  yellow: 'bg-amber-50 text-amber-700 border-amber-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

const DOT_CLASS: Record<SessionStatus['color'], string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
};

/**
 * Live-counting badge showing how long the 24h session window remains.
 * Ticks every minute via state. Compact variant just a colored dot.
 */
export function SessionBadge({
  lastInboundAt,
  sessionExpiresAt,
  compact = false,
}: {
  lastInboundAt: string | null | undefined;
  sessionExpiresAt: string | null | undefined;
  compact?: boolean;
}) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Recompute whenever the tick fires or props change.
  const s = useMemo(
    () => computeSession(lastInboundAt, sessionExpiresAt),
    [tick, lastInboundAt, sessionExpiresAt],
  );

  if (compact) {
    return (
      <span
        className={`inline-block h-2 w-2 rounded-full ${DOT_CLASS[s.color]}`}
        title={s.label}
        aria-label={`Session ${s.label}`}
      />
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${COLOR_CLASS[s.color]}`}
      title="24-hour customer service window — required by Meta for freeform messages"
    >
      <Clock className="h-3 w-3" />
      <span>{s.label}</span>
    </span>
  );
}
